// Helper functions used across multiple components

export const toTitleCaseWords = (value) =>
  String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * Friendly vehicle display name for booking forms.
 * Never includes engine size or fuel type.
 * @param {{ make?: string, model?: string, year?: string|number|null }} parts
 * @returns {string} e.g. "Renault Captur (2018)" or "Renault (2018)"
 */
export const formatVehicleDisplayName = ({ make, model, year } = {}) => {
  const name = [toTitleCaseWords(make), toTitleCaseWords(model)].filter(Boolean).join(' ');
  if (year) {
    return name ? `${name} (${year})` : `(${year})`;
  }
  return name;
};

/** Vehicle cost is stored as subtotal. Returns total (subtotal + VAT) using same rules as VehicleDetailModal: MOT no VAT, others use includeVat. */
export const getVehicleBillTotal = (veh) => {
  const subtotal = Number(veh?.cost) || 0;
  const vatEnabled = veh?.status === 'MOT' ? false : (veh?.includeVat !== undefined ? veh.includeVat : true);
  const vat = vatEnabled ? subtotal * 0.2 : 0;
  return subtotal + vat;
};

export const formatCurrency = (value) => {
  const amount = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(amount);
};

export const getDateRange = (period, selectedMonth = null, selectedYear = null, selectedYearFilter = null) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = new Date(today);

  switch (period) {
    case 'day':
      // startDate is already today at midnight, no change needed
      break;
    case 'week':
      // Last 7 days instead of calendar week
      startDate.setDate(today.getDate() - 6); // 7 days including today
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'month':
      if (selectedMonth !== null && selectedYear !== null) {
        // Specific month selected
        startDate.setFullYear(selectedYear, selectedMonth, 1);
        startDate.setHours(0, 0, 0, 0);
        // Set end date to last day of selected month
        const endDate = new Date(selectedYear, selectedMonth + 1, 0); // Day 0 = last day of previous month
        endDate.setHours(23, 59, 59, 999);
        return { startDate, endDate };
      } else {
        // Last 30 days instead of calendar month
        startDate.setDate(today.getDate() - 29); // 30 days including today
        startDate.setHours(0, 0, 0, 0);
      }
      break;
    case 'year':
      if (selectedYearFilter !== null) {
        // Specific year selected
        startDate.setFullYear(selectedYearFilter, 0, 1);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(selectedYearFilter, 11, 31); // December 31
        endDate.setHours(23, 59, 59, 999);
        return { startDate, endDate };
      } else {
        // Current year
        startDate.setMonth(0, 1);
        startDate.setHours(0, 0, 0, 0);
      }
      break;
    default:
      break;
  }

  const endDate = new Date(today);
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
};

// Status styling helper
export const getStatusStyle = (status) => {
  const STATUS_STYLES = {
    'Workshop': {
      gradient: 'linear-gradient(90deg, #4f8df3 0%, #73b0ff 100%)',
      pillBg: 'rgba(79, 141, 243, 0.18)',
      pillColor: '#3f63b8'
    },
    'Awaiting Parts': {
      gradient: 'linear-gradient(90deg, #ff9f43 0%, #ffc36a 100%)',
      pillBg: 'rgba(255, 168, 76, 0.22)',
      pillColor: '#d9792e'
    },
    'Sales': {
      gradient: 'linear-gradient(90deg, #f97316 0%, #fb923c 100%)',
      pillBg: 'rgba(249, 115, 22, 0.18)',
      pillColor: '#c2410c'
    },
    'MOT': {
      gradient: 'linear-gradient(90deg, #56c596 0%, #7ee0b3 100%)',
      pillBg: 'rgba(86, 197, 150, 0.22)',
      pillColor: '#279268'
    },
    'Bodyshop': {
      gradient: 'linear-gradient(90deg, #ec4899 0%, #f472b6 100%)',
      pillBg: 'rgba(236, 72, 153, 0.2)',
      pillColor: '#be185d'
    },
    'Tyres': {
      gradient: 'linear-gradient(90deg, #b45309 0%, #d97706 100%)',
      pillBg: 'rgba(180, 83, 9, 0.2)',
      pillColor: '#92400e'
    },
    'Archive': {
      gradient: 'linear-gradient(90deg, #6b7280 0%, #9ca3af 100%)',
      pillBg: 'rgba(107, 114, 128, 0.18)',
      pillColor: '#4b5563'
    }
  };

  return STATUS_STYLES[status] || {
    gradient: 'linear-gradient(90deg, var(--primary) 0%, #7aa7ff 100%)',
    pillBg: 'rgba(79, 141, 243, 0.18)',
    pillColor: '#3f63b8'
  };
};

/** Extract YYYY-MM-DD from a stored date string (handles ISO timestamps). */
export const toDateOnly = (value) => {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};

/** Today's calendar date as YYYY-MM-DD in the local timezone (not UTC). */
export const todayDateOnly = () => {
  const n = new Date();
  const mm = String(n.getMonth() + 1).padStart(2, '0');
  const dd = String(n.getDate()).padStart(2, '0');
  return `${n.getFullYear()}-${mm}-${dd}`;
};

/**
 * Parse YYYY-MM-DD as local midnight.
 * Avoids the UTC shift from `new Date('YYYY-MM-DD')` (which is UTC midnight).
 */
export const parseDateOnlyLocal = (value) => {
  const d = toDateOnly(value);
  if (!d) return null;
  const [y, m, day] = d.split('-').map(Number);
  if (!y || !m || !day) return null;
  const date = new Date(y, m - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Format a stored date-only / ISO date for display without timezone day-shift. */
export const formatDateOnlyDisplay = (value, options = { day: 'numeric', month: 'short', year: 'numeric' }) => {
  const date = parseDateOnlyLocal(value);
  if (!date) return '';
  return date.toLocaleDateString('en-GB', options);
};

/**
 * Normalize MOT booked time to local wall-clock `YYYY-MM-DDTHH:mm`.
 * Never round-trip wall-clock strings through `toISOString()` (that shifts by BST/GMT).
 */
export const normalizeMotTimeSlot = (timeSlot, fallbackDate = '') => {
  if (timeSlot == null || timeSlot === '') return '';
  const s = String(timeSlot).trim();

  // Instant with timezone (legacy / accidental ISO) → local wall-clock
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) && (/Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s))) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${day}T${hh}:${mm}`;
    }
  }

  // Wall-clock `YYYY-MM-DDTHH:mm` (ignore seconds) — take digits literally
  const withT = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{1,2}):(\d{2})/);
  if (withT) {
    return `${withT[1]}T${withT[2].padStart(2, '0')}:${withT[3]}`;
  }

  // Time-only `HH:mm`
  const timeOnly = s.match(/^(\d{1,2}):(\d{2})/);
  if (timeOnly) {
    const datePart = toDateOnly(fallbackDate) || todayDateOnly();
    return `${datePart}T${timeOnly[1].padStart(2, '0')}:${timeOnly[2]}`;
  }

  return '';
};

/** Value for `<input type="datetime-local">` — local wall-clock, no UTC conversion. */
export const toDatetimeLocalValue = (timeSlot, fallbackDate = '') =>
  normalizeMotTimeSlot(timeSlot, fallbackDate);

/** Display MOT booked date+time from a wall-clock timeSlot string. */
export const formatMotTimeSlotDisplay = (timeSlot, fallbackDate = '') => {
  const normalized = normalizeMotTimeSlot(timeSlot, fallbackDate);
  if (!normalized) return '';
  const [datePart, timePart] = normalized.split('T');
  const date = parseDateOnlyLocal(datePart);
  if (!date) return timePart || normalized;
  const [hh, mm] = timePart.split(':').map(Number);
  date.setHours(hh || 0, mm || 0, 0, 0);
  return date.toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/** Allowed MOT booking lengths (minutes). */
export const MOT_SLOT_DURATIONS = [10, 20, 30, 40];
export const DEFAULT_MOT_SLOT_DURATION = 30;

/**
 * Resolve MOT slot length for a vehicle (legacy bookings default to 30).
 * @param {{ motSlotDuration?: number|string }|null|undefined} vehicle
 * @returns {number}
 */
export const getMotSlotDuration = (vehicle) => {
  const d = parseInt(vehicle?.motSlotDuration, 10);
  return MOT_SLOT_DURATIONS.includes(d) ? d : DEFAULT_MOT_SLOT_DURATION;
};

/** Minutes from midnight for HH:mm (or YYYY-MM-DDTHH:mm). */
export const motTimeToMinutes = (timeOrSlot) => {
  if (timeOrSlot == null || timeOrSlot === '') return null;
  const s = String(timeOrSlot).trim();
  const m = s.match(/(?:T|^)(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

/**
 * Generate MOT start-time options on a 10-minute grid (9:00–17:30).
 * @param {string} selectedDate YYYY-MM-DD
 * @returns {{ time: string, displayTime: string, key: string }[]}
 */
export const generateMotTimeSlots = (selectedDate) => {
  if (!selectedDate) return [];
  const slots = [];
  for (let hour = 9; hour <= 17; hour++) {
    for (let min = 0; min < 60; min += 10) {
      if (hour === 17 && min > 30) break;
      const timeString = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      const h12 = hour % 12 || 12;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      slots.push({
        time: timeString,
        displayTime: `${h12}:${String(min).padStart(2, '0')} ${ampm}`,
        key: `${selectedDate}T${timeString}`,
      });
    }
  }
  return slots;
};

/**
 * True when candidate booking overlaps an existing MOT booking on that date.
 * @param {string} slotKey YYYY-MM-DDTHH:mm or HH:mm
 * @param {string} selectedDate YYYY-MM-DD
 * @param {number} durationMinutes 10 | 20 | 30 | 40
 * @param {Array} vehicles
 * @param {string|null} [excludeVehicleId]
 */
export const isMotTimeSlotUnavailable = (
  slotKey,
  selectedDate,
  durationMinutes,
  vehicles,
  excludeVehicleId = null,
) => {
  const start = motTimeToMinutes(slotKey);
  if (start == null || !selectedDate) return false;
  const duration = MOT_SLOT_DURATIONS.includes(durationMinutes)
    ? durationMinutes
    : DEFAULT_MOT_SLOT_DURATION;
  const end = start + duration;

  return (vehicles || []).some((vehicle) => {
    if (vehicle?.status !== 'MOT') return false;
    if (excludeVehicleId && (vehicle.id === excludeVehicleId || vehicle.__id === excludeVehicleId)) {
      return false;
    }
    const vehicleDate = vehicle.dropOffDate || vehicle.receivedDate;
    if (vehicleDate !== selectedDate) return false;
    if (!vehicle.timeSlot) return false;
    const otherStart = motTimeToMinutes(vehicle.timeSlot);
    if (otherStart == null) return false;
    const otherEnd = otherStart + getMotSlotDuration(vehicle);
    return start < otherEnd && end > otherStart;
  });
};

/**
 * True when "next service due" is strictly before today (local calendar).
 * No automatic updates — staff must confirm when the service was done.
 */
export const isNextServiceOverdue = (nextServiceDue) => {
  const d = toDateOnly(nextServiceDue);
  if (!d) return false;
  const [yy, mm, dd] = d.split('-').map(Number);
  const dueUtc = Date.UTC(yy, mm - 1, dd);
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return dueUtc < todayUtc;
};

/** Add calendar months to a YYYY-MM-DD string; returns YYYY-MM-DD (local calendar, day clamped). */
export const addCalendarMonthsDateOnly = (isoDateStr, monthsToAdd) => {
  const d = toDateOnly(isoDateStr);
  if (!d) return '';
  const parts = d.split('-').map(Number);
  const y = parts[0];
  const m0 = parts[1] - 1;
  const day = parts[2];
  const lastDay = new Date(y, m0 + monthsToAdd + 1, 0).getDate();
  const dayClamped = Math.min(day, lastDay);
  const out = new Date(y, m0 + monthsToAdd, dayClamped);
  const mm = String(out.getMonth() + 1).padStart(2, '0');
  const dd = String(out.getDate()).padStart(2, '0');
  return `${out.getFullYear()}-${mm}-${dd}`;
};

// Status icon helper
export const getStatusIcon = (status) => {
  const icons = {
    'MOT': <path d="M9 11l3 3L22 4"></path>,
    'Bodyshop': <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>,
    'Workshop': <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>,
    'Tyres': <circle cx="12" cy="12" r="8"></circle>,
    'Awaiting Parts': <circle cx="12" cy="12" r="10"></circle>,
    'Sales': <path d="M9 11l3 3L22 4"></path>
  };
  return icons[status] || <circle cx="12" cy="12" r="10"></circle>;
};
